import { ConsignmentApiModel, MarketplaceOrderApiModel } from '~/lib/api/smartsend/types';
import { OverflowContract } from './types';

/**
 * Floor for how many parcel options a consignment offers when padding is available. A single
 * quantity-1 order can still need several physical boxes, so that choice must not be capped at one.
 */
export const MIN_PARCEL_OPTIONS = 10;

const unitsIn = (rows: { Quantity?: number }[]) => rows.reduce((total, row) => total + (row.Quantity || 1), 0);

/**
 * How many parcels the user may choose. This follows the OVERFLOW CONTRACT the flow will allocate
 * under, not the outcome - which is the correction here. It used to key off PlanOutcome and floor
 * ONE_SHIPMENT at MIN_PARCEL_OPTIONS unconditionally, but ONE_SHIPMENT is only sometimes padded:
 * GenerateConsignmentFromItem pads a single order line and clamps everything else, and
 * RedistributeConsignmentParcels always clamps. So the picker offered up to ten options while
 * distributeIntoParcels silently clamped at the piece count, and every option above that count
 * produced an identical plan - selecting it appeared to do nothing at all.
 *
 * `allocatablePieces` is whatever the caller's flow can actually split: unit count for the flows that
 * break rows apart, row count for the ones that move whole rows.
 */
export function parcelCountBound(overflow: OverflowContract, allocatablePieces: number): number {
  if (overflow === 'pad-with-copies') {
    return Math.max(MIN_PARCEL_OPTIONS, allocatablePieces);
  }

  return Math.max(1, allocatablePieces);
}

/**
 * Whether surplus parcels can be padded out with copies, which needs exactly one order line.
 *
 * With several lines there is no way to know which line a surplus parcel would be for, so the parcel
 * would have to go out empty - and an empty parcel is priced by QuoteHelper.MapQuoteRequest and then
 * dropped by OrderMapper.MapParcels, which is precisely what ITEM-PARCEL-EMPTY exists to prevent.
 * Padding a multi-line order is therefore not merely unimplemented, it is incoherent.
 */
export const canPadAcrossParcels = (lines: MarketplaceOrderApiModel[]): boolean => lines?.length === 1;

/**
 * The parcel-count range for a multi-parcel consignment, matching what the two generators will
 * actually produce for this item.
 *
 * Redistributing an existing consignment always clamps, and it clamps against the CONSIGNMENT's own
 * units rather than the order's - rows may have been split, padded or added by hand since. Note the
 * consequence: padding is a one-way step, because once padded copies exist they are what redistribution
 * has to work from. Picking 1 clears the consignment, which puts the full padded range back.
 */
export function consignmentParcelBound(lines: MarketplaceOrderApiModel[], existing?: ConsignmentApiModel): number {
  if (existing?.Parcels?.length > 0) {
    return parcelCountBound('clamp', unitsIn(existing.Parcels.flatMap((parcel) => parcel.Contents ?? [])));
  }

  return parcelCountBound(canPadAcrossParcels(lines) ? 'pad-with-copies' : 'clamp', unitsIn(lines ?? []));
}
